import { MapPin, Award, Handshake } from 'lucide-react';
import PageLayout, { PageCTA } from '../components/PageLayout';
import { useLang } from '../i18n/LanguageContext';

// Photo assets (hosted on Emergent CDN)
const PHOTOS = [
  'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/uhoasgbu_Image_20260820194250_41_5.jpg',
  'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/36plyhct_Image_20260820194116_34_5.jpg',
  'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/34dltqo2_Image_20260820194228_40_5.jpg',
  'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/w4p5gca0_Image_20260820194156_38_5.jpg',
  'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/b3w0czon_Image_20260820194052_32_5.jpg',
];

export default function ExperiencePage() {
  const { t } = useLang();
  const p = t('pages.experience');
  const photos = p.photos || [];

  return (
    <PageLayout
      crumb={p.breadcrumb}
      title={p.hero}
      subtitle={p.sub}
      heroImage={PHOTOS[4]}
    >
      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow mb-6">
            <Handshake className="w-6 h-6 text-brand-navy" />
          </div>
          <h2 data-testid="experience-intro-title" className="text-3xl md:text-4xl font-bold text-brand-navy mb-4">{p.intro1Title}</h2>
          <p className="text-gray-700 leading-relaxed text-lg max-w-3xl mx-auto">{p.intro1Body}</p>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-gray-50" data-testid="experience-gallery">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-12">
            <Award className="w-6 h-6 text-brand-yellow" />
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy">{p.galleryTitle}</h2>
          </div>

          <div className="space-y-14">
            {photos.map((ph, i) => {
              const reverse = i % 2 === 1;
              return (
                <article
                  key={i}
                  data-testid={`experience-photo-${i}`}
                  className={`grid lg:grid-cols-5 gap-8 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}
                >
                  <div className="lg:col-span-3 relative group">
                    <div className="absolute -inset-1 bg-gradient-to-tr from-brand-yellow/40 to-brand-navy/10 rounded-2xl blur opacity-40 group-hover:opacity-70 transition" />
                    <img
                      src={PHOTOS[i]}
                      alt={ph.title}
                      loading="lazy"
                      className="relative w-full h-[360px] md:h-[440px] object-cover rounded-2xl shadow-2xl"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow text-brand-navy text-[11px] font-bold tracking-wider uppercase">
                      <MapPin className="w-3.5 h-3.5" /> {ph.place}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight mb-4">
                      {ph.title}
                    </h3>
                    <div className="w-16 h-1 bg-brand-yellow rounded-full mb-4" />
                    <p className="text-gray-700 leading-relaxed">{ph.caption}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <PageCTA title={p.ctaTitle} body={p.ctaBody} />
    </PageLayout>
  );
}
